import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "May 1, 2025";
const EMAIL = "privacy@getcitizen.ai";

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <PublicHeader />

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-8 sm:p-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
            Legal
          </p>
          <h1 className="text-3xl font-bold font-display text-slate-900 mb-1">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-400 mb-10">
            Effective {EFFECTIVE_DATE}
          </p>

          <Prose>
            <Section title="1. Information We Collect">
              We collect information you provide directly when using the Service:
              <ul>
                <li><strong>Account info</strong> — name and email address from Google Sign-In.</li>
                <li><strong>Application data</strong> — personal information you enter to complete Form N-400 (name, address history, employment, travel, marital history, and similar).</li>
                <li><strong>Payment info</strong> — billing is handled by Stripe. We receive a payment confirmation and the last four digits of your card; we never store full card numbers.</li>
                <li><strong>Usage data</strong> — pages visited, actions taken, timestamps, and browser/device type for debugging and improving the Service.</li>
              </ul>
            </Section>

            <Section title="2. How We Use Your Information">
              We use your data to:
              <ul>
                <li>Generate your completed N-400 PDF packet.</li>
                <li>Process payments and send receipts.</li>
                <li>Send transactional emails (payment confirmation, download link, progress reminders).</li>
                <li>Improve and debug the Service.</li>
                <li>Comply with legal obligations.</li>
              </ul>
              We do <strong>not</strong> sell, rent, or share your personal information with third parties for marketing purposes.
            </Section>

            <Section title="3. Data Storage and Security">
              Your data is stored in encrypted databases hosted on infrastructure in the United States.
              We use TLS 1.2+ for all data in transit and AES-256 encryption at rest.
              Access to production data is restricted to authorized personnel only.
            </Section>

            <Section title="4. Data Retention">
              We retain your case data for as long as your account is active, or for up to
              3 years after your last activity, whichever is later. You may request deletion
              of your data at any time by contacting us (see Section 8). Note that some
              records may be retained longer where required by law.
            </Section>

            <Section title="5. Third-Party Services">
              We use the following third-party processors:
              <ul>
                <li><strong>Google OAuth</strong> — for authentication.</li>
                <li><strong>Stripe</strong> — for payment processing. Subject to Stripe's Privacy Policy.</li>
                <li><strong>Email provider</strong> — for transactional emails.</li>
              </ul>
              Each provider is contractually bound to handle your data in compliance with
              applicable privacy laws.
            </Section>

            <Section title="6. Cookies">
              We use session cookies required for authentication. We do not use
              advertising or tracking cookies. You can disable cookies in your browser
              settings, but this will prevent you from signing in.
            </Section>

            <Section title="7. Your Rights">
              Depending on your jurisdiction, you may have the right to:
              <ul>
                <li>Access the personal data we hold about you.</li>
                <li>Correct inaccurate data.</li>
                <li>Request deletion of your data ("right to be forgotten").</li>
                <li>Export your data in a portable format.</li>
                <li>Object to certain processing activities.</li>
              </ul>
              To exercise any of these rights, email us at{" "}
              <a href={`mailto:${EMAIL}`} className="text-blue-600 hover:underline">{EMAIL}</a>.
              We will respond within 30 days.
            </Section>

            <Section title="8. Children's Privacy">
              The Service is not directed to individuals under 18. We do not knowingly
              collect personal data from minors. If you believe a minor has provided us
              with personal information, contact us and we will delete it promptly.
            </Section>

            <Section title="9. Changes to This Policy">
              We may update this Privacy Policy periodically. We will notify you of
              material changes by email or by a prominent notice on the Service.
              The effective date at the top of this page indicates when the policy
              was last revised.
            </Section>

            <Section title="10. Contact Us">
              Privacy questions or requests:{" "}
              <a href={`mailto:${EMAIL}`} className="text-blue-600 hover:underline">
                {EMAIL}
              </a>
            </Section>
          </Prose>

          <div className="mt-10 pt-6 border-t border-slate-100 flex items-center gap-4 text-sm">
            <Link to="/terms" className="text-blue-600 hover:text-blue-700">
              Terms of Service →
            </Link>
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600 ml-auto">
              ← Back to app
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PublicHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shadow-sm transition-transform group-hover:scale-105">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 1v14M1 8h14M4.5 4.5l7 7M11.5 4.5l-7 7" />
            </svg>
          </div>
          <span className="text-base font-bold tracking-tight text-slate-900 font-display">
            GetCitizen <span className="text-blue-600">AI</span>
          </span>
        </Link>
      </div>
    </header>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-7 text-slate-700 text-sm leading-relaxed [&_ul]:mt-2 [&_ul]:space-y-1.5 [&_ul]:pl-4 [&_ul]:list-disc [&_ul]:marker:text-slate-300">
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-semibold font-display text-slate-900 text-base mb-2">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
